import { useEffect, useMemo } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { channel } from '../../utils/broadcast';
import { useAuthStore } from '../../stores/useAuthStore';
import { useScreenStore, isSocketScreen } from '../../stores/useScreenStore';
import { useCurrentHashStore } from '../../stores/hashs/useCurrentHashStore';
import { useOperationalMapStore } from '../../stores/useOperationalMapStore';
import { escaletedLinesTodayConfig, HASH_CONFIGS } from '../../utils/hashs/hashConfig';
import { usePermissions } from '../auth/usePermissions';

export function useHashNavigation() {

    const navigate = useNavigate();

    const { hash } = useLocation();

    const user = useAuthStore(state => state.user);

    const allowPerfis = useAuthStore(state => state.allowPerfis);

    const monitorMode = useScreenStore(state => state.monitorMode);

    const screen = useScreenStore(state => state.screen);

    const currentHash = useCurrentHashStore(state => state.currentHash);

    const { canTripsOnly } = usePermissions();

    const isOperationalMapMode = useOperationalMapStore(
        state => state.isOperationalMapMode
    );

    const hashSet = useMemo(
        () =>
            new Set(
                (
                    (canTripsOnly || isOperationalMapMode)
                        ? [escaletedLinesTodayConfig]
                        : HASH_CONFIGS
                ).map(config => config.hash)
            ),
        [canTripsOnly, isOperationalMapMode]
    );

    useEffect(() => {

        if (!user) {
            return;
        }

        const currentHash = useCurrentHashStore.getState().currentHash;

        if (!hashSet.has(currentHash)) {
            useCurrentHashStore
                .getState()
                .setCurrentHash(escaletedLinesTodayConfig.hash);
        }

    }, [user, hashSet]);

    useEffect(() => {

        if (!user || !allowPerfis.size || monitorMode === 0 || !isSocketScreen(screen)) {
            return;
        }

        const navTo = (screen === 'Home') ? '/' : '/filters/';

        if (hashSet.has(currentHash)) {
            navigate(`${navTo}#${currentHash}`, {replace: true});
        }

        if(screen === 'Home') return;

        channel.postMessage({
            tipo: 'hash',
            payload: {
                screen,
                hash: currentHash
            }
        });

    }, [
        user,
        hashSet,
        allowPerfis,
        monitorMode,
        currentHash,
        screen,
        navigate
    ]);

    useEffect(() => {

        const urlHash = hash.replace(/^#/, '');

        const currentHash = useCurrentHashStore
            .getState()
            .currentHash;

        if(urlHash === currentHash){
            return;
        }

        if(hashSet.has(urlHash)){
            useCurrentHashStore
                .getState()
                .setCurrentHash(urlHash);
        }

    }, [hash, hashSet]);

    useEffect(() => {

        const handleChannelMessage = ({ data }) => {

            const {
                tipo,
                payload
            } = data;

            if (tipo !== 'hash' || !isSocketScreen(payload?.screen) || (payload.screen === screen)) {
                return;
            }

            if (screen === 'Home') {
                useCurrentHashStore
                    .getState()
                    .setCurrentHash(payload.hash);
            }

        };

        channel.addEventListener('message', handleChannelMessage);

        return () => {

            channel.removeEventListener('message', handleChannelMessage);

        };

    }, [
        screen,
        navigate
    ]);

}